import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { MeterService } from '@services/meter.service';
import { LoginService } from '@services/login.service';
import { VerificationResponse, VerificationStatus } from './verification-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class VerificationAdapterService {
  private readonly loginService = inject(LoginService);

  constructor(private meterService: MeterService) {}

  /**
   * Adapter for LoA verification polling
   */
  createConsentPollingFunction(requestId: string): () => Observable<VerificationResponse> {
    return () => {
      return this.meterService.checkConsentStatus(requestId).pipe(
        map((response: any) => {
          const loaStatus = response.message?.consentStatus;
          const processingStatus = response.message?.processingStatus;
          const meterConsents = response.message?.meterConsents || {};
          
          // Check if all meters have failed
          const allMetersFailed = Object.keys(meterConsents).length > 0 && 
            Object.values(meterConsents).every(status => status === 'FAILED');
          
          if (loaStatus === 'APPROVED') {
            // LOA is approved. Check for processing status
            return {
              status: VerificationStatus.SUCCESS,
              message: 'Consent Verification completed successfully.',
              finalStatus: true
            };
          } else if (loaStatus === 'REJECTED' || loaStatus === 'FAILED' || 
                     processingStatus === 'FAILED' || allMetersFailed) {
            // LOA is rejected or failed, or processing failed, or all meters failed
            return {
              status: VerificationStatus.FAILURE,
              message: 'Consent Verification failed. Please check your input and try again.',
              finalStatus: true
            };
          } else {
            // Still in progress or unknown status
            return {
              status: VerificationStatus.PENDING,
              message: 'Consent Verification in progress...',
              finalStatus: false
            };
          }
        })
      );
    };
  }

  /**
   * Adapter for Digital Signature verification polling
   */
  createDigitalSignaturePollingFunction(consentId: string): () => Observable<VerificationResponse> {
    return () => {
      return this.meterService.checkDigitalSignatureStatus(consentId).pipe(
        map((response: any) => {
          const consentStatus = response.consentStatus;
          
          if (consentStatus === 'signed') {
            return {
              status: VerificationStatus.SUCCESS,
              message: 'Digital signature completed successfully.',
              finalStatus: true
            };
          } else if (consentStatus === 'error') {
            return {
              status: VerificationStatus.ERROR,
              message: 'Error checking digital signature status. Please try again.',
              finalStatus: true
            };
          } else if (consentStatus === 'pending') {
            return {
              status: VerificationStatus.PENDING,
              message: 'Digital signature is still pending. Please complete the signing process.',
              finalStatus: false
            };
          } else {
            return {
              status: VerificationStatus.FAILURE,
              message: `Digital signature verification failed. Status: ${consentStatus}`,
              finalStatus: true
            };
          }
        })
      );
    };
  }
}
